import { useState } from 'react';

import { SHA1 } from 'crypto-js';

import { getFilestackClient as getFileStackClient } from 'lib/clients/filestack/filestackClient';
import { ia } from 'lib/helpers/utility';

import { showAlert } from 'components/shared/Alert/Alert';

import useLoading from './useLoading';

export const useFileStack = (options = {}) => {
  const [files, setFiles] = useState([]);
  const [notUploaded, setNotUploaded] = useState([]);
  const { loading, startLoading, stopLoading } = useLoading();

  const updateFileState = (fileId, updates) => {
    setFiles((prev) =>
      prev.map((file) => (file.fileId === fileId ? { ...file, ...updates } : file))
    );
  };

  const handleUploadProgress = (fileId, event) => {
    const status = event.totalPercent === 100 ? 'uploaded' : 'uploading';
    updateFileState(fileId, {
      progress: event.totalPercent,
      uploadedSize: event.totalBytes,
      status
    });
  };

  const handleUploadSuccess = (uploadedFile, onUpload) => {
    setFiles((prev) =>
      prev.map((file) => (file.fileId === uploadedFile.fileId ? { ...uploadedFile } : file))
    );
    setNotUploaded((prev) => prev.filter((file) => file.fileId !== uploadedFile.fileId));

    if (onUpload) onUpload(uploadedFile);
  };

  const addFile = async (newFile, onUpload) => {
    const fileStackClient = await getFileStackClient();
    if (!fileStackClient) return;

    if (newFile) {
      const fileId = SHA1(new Date().toString() + newFile.name).toString();

      setNotUploaded((prev) => [...prev, { fileId, originalFile: newFile }]);
      const fileData = {
        fileId,
        name: newFile.name,
        size: newFile.size,
        uploadedSize: 0,
        type: newFile.type,
        status: 'uploading',
        progress: 0
      };
      setFiles((prev) => [...prev, fileData]);

      try {
        const response = await fileStackClient.upload(newFile, {
          ...options,
          onProgress: (event) => handleUploadProgress(fileId, event)
        });

        const uploadedFile = {
          fileId,
          handle: response.handle,
          name: newFile.name,
          size: newFile.size,
          uploadedSize: newFile.size,
          type: newFile.type,
          url: response.url,
          status: 'uploaded',
          progress: 100,
          key: response.key
        };
        handleUploadSuccess(uploadedFile, onUpload);
        return uploadedFile;
      } catch (error) {
        updateFileState(fileId, { status: 'failed' });
        showAlert({
          color: 'danger',
          message: `Failed to upload ${newFile.name}. Please try again.`
        });
        return null;
      }
    }
  };

  const addFiles = async (newFiles, onUpload, onUploadMultiple) => {
    const fileStackClient = await getFileStackClient();
    if (!fileStackClient) return;

    startLoading();
    try {
      const fileDataArray = newFiles.map((newFile) => {
        const fileId = SHA1(new Date().toString() + newFile.name).toString();
        return {
          fileId,
          name: newFile.name,
          size: newFile.size,
          uploadedSize: 0,
          type: newFile.type,
          status: 'uploading',
          progress: 0,
          originalFile: newFile,
          key: newFile.key || null
        };
      });

      setNotUploaded((prev) => [
        ...prev,
        ...fileDataArray.map(({ fileId, originalFile }) => ({ fileId, originalFile }))
      ]);
      setFiles((prev) => [...prev, ...fileDataArray]);

      const results = await Promise.allSettled(
        newFiles.map(async (newFile, index) => {
          const fileId = fileDataArray[index].fileId;
          const response = await fileStackClient.upload(newFile, {
            ...options,
            onProgress: (event) => handleUploadProgress(fileId, event)
          });

          return { fileId, response, newFile };
        })
      );

      const responses = [];
      const uploadedFiles = [];
      results.forEach((result, index) => {
        const fileId = fileDataArray[index].fileId;
        if (result.status === 'fulfilled') {
          const { response, newFile } = result.value;
          const uploadedFile = {
            fileId,
            handle: response.handle,
            name: newFile.name,
            size: newFile.size,
            uploadedSize: newFile.size,
            type: newFile.type,
            url: response.url,
            status: 'uploaded',
            progress: 100,
            key: response.key || null
          };
          handleUploadSuccess(uploadedFile, onUpload);
          responses.push(response);
          uploadedFiles.push(uploadedFile);
        } else {
          updateFileState(fileId, { status: 'failed' });
          showAlert({
            color: 'danger',
            message: `Failed to upload ${fileDataArray[index].name}. Please try again.`
          });
        }
      });

      if (onUploadMultiple) {
        onUploadMultiple(uploadedFiles);
      }

      if (results.some((result) => result.status === 'rejected')) {
        showAlert({
          color: 'danger',
          message: 'Some files failed to upload. You can retry failed uploads.'
        });
      }

      return responses;
    } catch (error) {
      showAlert({ color: 'danger', message: 'An error occurred during upload. Please try again.' });
      return null;
    } finally {
      stopLoading();
    }
  };

  const openFilePicker = async (onUpload, onUploadMultiple) => {
    const fileStackClient = await getFileStackClient();
    if (!fileStackClient) return;

    const defaultOptions = {
      accept: ['image/*', '.pdf', '.png', '.jpeg'],
      maxFiles: 10,
      uploadInBackground: false,
      exposeOriginalFile: true,
      onUploadDone: (data) => {
        if (data && ia(data.filesUploaded)) {
          const newFiles = data.filesUploaded.map((uploaded) => {
            const fileId = SHA1(new Date().toString()).toString();
            return {
              fileId,
              handle: uploaded.handle,
              name: uploaded.filename,
              size: uploaded.size,
              uploadedSize: uploaded.size,
              type: uploaded.mimetype,
              url: uploaded.url,
              status: 'uploaded',
              progress: 100
            };
          });

          setFiles((prev) => [...prev, ...newFiles]);
          newFiles.forEach((fileData) => handleUploadSuccess(fileData, onUpload));

          if (onUploadMultiple) {
            onUploadMultiple(newFiles);
          }
        }
      },
      onFileUploadFailed: () => {
        showAlert({ color: 'danger', message: 'Failed to upload some files. Please try again.' });
      }
    };

    fileStackClient.picker({ ...defaultOptions, ...options }).open();
  };

  const deleteFile = async (fileHandle, onDelete) => {
    startLoading();
    try {
      await onDelete(fileHandle);
      if (ia(fileHandle)) {
        setFiles([]);
      } else {
        setFiles((prev) => prev.filter((file) => file.handle !== fileHandle));
      }
      return true;
    } catch (err) {
      showAlert({ color: 'danger', message: err.message || 'Failed to delete' });
      return false;
    } finally {
      stopLoading();
    }
  };

  const readFile = async (fileHandle) => {
    const existingFile = files.find((file) => file.handle === fileHandle);
    if (existingFile) return existingFile;

    startLoading();
    try {
      const fileStackClient = await getFileStackClient();
      const result = await fileStackClient.download(fileHandle);

      const file = new File([result.data], result.headers['x-file-name'], {
        type: result.headers['content-type']
      });
      const fileId = SHA1(new Date().toString()).toString();

      const fileData = {
        fileId,
        handle: fileHandle,
        name: file.name,
        size: file.size,
        uploadedSize: file.size,
        type: file.type,
        url: result.config.url,
        progress: 100,
        status: 'uploaded'
      };
      setFiles((prev) => {
        const fileMap = new Map(prev.map((file) => [file.handle, file]));
        fileMap.set(fileData.handle, fileData);
        return Array.from(fileMap.values());
      });
      return fileData;
    } catch (err) {
      showAlert({ color: 'danger', message: err.message || 'Failed to read file.' });
      return null;
    } finally {
      stopLoading();
    }
  };

  const retryUpload = async (fileId, onUpload) => {
    const file = notUploaded.find((file) => file.fileId === fileId);
    if (!file) return;

    setFiles((prev) => prev.filter((file) => file.fileId !== fileId));
    addFile(file.originalFile, onUpload);
  };

  return {
    files,
    setFiles,
    addFile,
    addFiles,
    deleteFile,
    readFile,
    retryUpload,
    openFilePicker,
    loading
  };
};
